import type { AlertIssue } from '../store/types';
import { mapFieldRefToSelector } from './fieldMapper';

/** Which Features table cell an alert issue targets for a given feature row. */
export type FeatureAlertCellSurface = 'ga' | 'demo' | 'prd';

/** Maps the fieldRef dateType token (mapFieldRefToSelector's `subfield`) to the Features table cell. */
const SUBFIELD_TO_CELL_SURFACE: Record<string, FeatureAlertCellSurface> = {
  ga: 'ga',
  'demo.eta': 'demo',
  'prd.eta': 'prd'
};

export interface FeatureAlertTarget {
  featureId: string;
  surface: FeatureAlertCellSurface;
}

/**
 * Resolves which feature row + cell surface (if any) an alert issue targets, or null when the
 * issue isn't a feature-shaped fieldRef at all.
 *
 * Mirrors `milestoneDeclarativeHighlight.shouldHighlightMilestoneAlertCell` — Features rows are
 * dynamically rendered (fetched via `FeaturesContainer`'s `fetchFeaturesByIssue`), so highlighting
 * is computed declaratively per-render (by `Features.tsx`, via `useActiveAlertIssues`) rather than
 * through the DOM-query `highlightField()` flow in fieldHighlighter.ts, which is unreliable for
 * elements that may not exist yet when the highlight effect runs.
 *
 * Takes no `featureId` argument (unlike an earlier version) so callers can build a featureId →
 * surface → issue map in one O(issues) pass instead of re-scanning all issues once per row.
 */
export function getFeatureAlertTarget(
  issue: AlertIssue
): FeatureAlertTarget | null {
  const mapping = mapFieldRefToSelector(issue.fieldRef, issue.ruleId);
  if (!mapping || mapping.section !== 'features') return null;
  if (!mapping.featureId || !mapping.subfield) return null;
  const surface = SUBFIELD_TO_CELL_SURFACE[mapping.subfield];
  if (!surface) return null;
  return { featureId: mapping.featureId, surface };
}
